import type { TutorLocation, TutorSearchContext } from "../tutorClient";

export const identifier = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const turnId = /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;

/** Search text stays in router state, not URLs/referrers or route analytics. */
export function tutorRouteLocation(pathname: string, search: string, state: unknown): TutorLocation | null {
  if (pathname === "/tutor" || pathname === "/tutor/") return { view: "home" };
  if (pathname === "/tutor/search") {
    const query = state && typeof state === "object" && "tutorSearch" in state ? state.tutorSearch : "";
    return typeof query === "string" && query.length <= 256 ? { view: "search", query } : null;
  }
  const parameters = new URLSearchParams(search);
  const messageId = parameters.get("turn") ?? undefined;
  if (messageId && !turnId.test(messageId)) return null;
  const scope = pathname === "/tutor/general" ? { kind: "general" as const } :
    pathname.startsWith("/tutor/class/") && identifier.test(pathname.slice("/tutor/class/".length)) ?
      { kind: "class" as const, classId: pathname.slice("/tutor/class/".length) } : null;
  if (!scope) return null;
  const reviewKeys = ["review", "document", "episode"] as const;
  const reviewValues = reviewKeys.map(key => parameters.get(key));
  if (reviewValues.some(Boolean) && !reviewValues.every(value => value && identifier.test(value))) return null;
  const examId = parameters.get("exam") ?? undefined;
  if (parameters.has("exam") && (!examId || !identifier.test(examId) || scope.kind !== "class" ||
      messageId || reviewValues.some(Boolean) || parameters.getAll("exam").length !== 1)) return null;
  const searchContext = messageId ? tutorSearchContext(state) : null;
  return { view: "conversation", scope, ...(messageId ? { messageId } : {}),
    ...(examId ? { examId } : {}),
    ...(reviewValues.every(Boolean) ? { reviewPath: { reviewId: reviewValues[0]!, documentId: reviewValues[1]!, episodeId: reviewValues[2]! } } : {}),
    ...(searchContext ? { search: searchContext } : {}) };
}

/** The match bar's words and result ids, from router state only (never the URL). Invalid context is dropped. */
function tutorSearchContext(state: unknown): TutorSearchContext | null {
  const value = state && typeof state === "object" && "tutorSearchContext" in state ? (state as { tutorSearchContext: unknown }).tutorSearchContext : null;
  if (!value || typeof value !== "object") return null;
  const { query, hitIds } = value as { query?: unknown; hitIds?: unknown };
  if (typeof query !== "string" || !query.trim() || query.length > 256 || !Array.isArray(hitIds) || hitIds.length > 50 ||
      !hitIds.every(id => typeof id === "string" && turnId.test(id))) return null;
  return { query, hitIds: [...hitIds] };
}

export function tutorRouteTarget(location: TutorLocation): { pathname: string; search: string; state: unknown } {
  if (location.view === "home") return { pathname: "/tutor", search: "", state: null };
  if (location.view === "search") {
    if (typeof location.query !== "string" || location.query.length > 256) throw new Error("tutor_location_invalid");
    return { pathname: "/tutor/search", search: "", state: { tutorSearch: location.query } };
  }
  const pathname = location.scope.kind === "general" ? "/tutor/general" : `/tutor/class/${location.scope.classId}`;
  const parameters = new URLSearchParams();
  if (location.messageId) parameters.set("turn", location.messageId);
  if (location.examId) parameters.set("exam", location.examId);
  if (location.reviewPath) {
    parameters.set("review", location.reviewPath.reviewId);
    parameters.set("document", location.reviewPath.documentId);
    parameters.set("episode", location.reviewPath.episodeId);
  }
  const search = parameters.size ? `?${parameters}` : "";
  if (!tutorRouteLocation(pathname, search, null)) throw new Error("tutor_location_invalid");
  // A chat opened from search keeps its words and results in router state, for the match bar.
  const state = location.messageId && location.search ? { tutorSearchContext: { query: location.search.query, hitIds: [...location.search.hitIds] } } : null;
  if (state && !tutorSearchContext(state)) throw new Error("tutor_location_invalid");
  return { pathname, search, state };
}
